'use client';

import { c as _c } from "react/compiler-runtime";
import { css, cx } from '@repo/pandacss/css';
import { Flex, Stack, VisuallyHidden } from '@repo/pandacss/jsx';
import type { WithCss } from '@repo/pandacss/types';
import { CTASearchParams } from '@repo/platform-analytics/lib/ctaSearchParams';
import { FinishType, type LinkType } from '@repo/platform-analytics/types';
import { MaskIcon } from '@repo/ui/primitives/MaskIcon';
import { Typography } from '@repo/ui/Typography';
import type { ComponentPropsWithRef } from 'react';
import { type CtaAnalyticsProps, CtaLink } from '../CTA/CtaLink';
import { RatesAndFeesLink } from '../RatesAndFeesLink';
const applyNowLinkBaseRaw = css.raw({
  alignItems: 'center',
  appearance: 'button',
  borderRadius: 'xs',
  display: 'flex',
  fontSize: '[0.8125rem]',
  fontWeight: '[700]',
  gap: '2',
  justifyContent: 'center',
  letterSpacing: '[0.0625em]',
  minWidth: '[150px]',
  py: '[0.8rem]',
  textDecoration: 'none',
  textTransform: 'uppercase',
  touchAction: 'manipulation',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  _hover: {
    textDecoration: 'none'
  }
});
const primaryLinkRaw = css.raw(applyNowLinkBaseRaw, {
  backgroundColor: 'primary.main',
  color: 'common.white',
  transitionProperty: '[background-color]',
  transitionDuration: '[0.15s]',
  transitionTimingFunction: '[ease-out]',
  _hover: {
    backgroundColor: 'primary.dark',
    color: 'common.white'
  },
  _active: {
    backgroundColor: 'primary.main'
  },
  _focus: {
    color: 'common.white'
  },
  _visited: {
    color: 'common.white'
  }
});
const primaryLinkClass = css(primaryLinkRaw);
const secondaryLinkClass = css(applyNowLinkBaseRaw, {
  backgroundColor: 'common.white',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'primary.main',
  color: 'primary.main',
  _hover: {
    outlineWidth: '1px',
    outlineStyle: 'solid',
    outlineColor: 'primary.main'
  },
  _visited: {
    color: 'primary.main'
  }
});
const phoneNumberLinkClass = css({
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  touchAction: 'manipulation',
  color: 'secondary.main',
  _hover: {
    textDecoration: 'underline'
  },
  _focus: {
    borderRadius: '[4px]'
  }
});
const applyNowWrapperBaseRaw = css.raw({
  display: 'flex',
  flexDirection: 'column',
  gap: '2'
});
const bottomTextRowClass = css({
  display: 'flex',
  flexWrap: 'wrap',
  alignSelf: 'center',
  justifyContent: 'center'
});
const bottomTextClass = css({
  color: 'text.secondary',
  textAlign: 'center',
  fontSize: {
    base: '[13px]',
    _pcMd: '[12px]'
  }
});
const termsFeesVisibleOnMobileClass = css({
  mdDown: {
    display: 'block'
  },
  _pcMobile: {
    display: 'block'
  }
});
const termsFeesHiddenOnMobileClass = css({
  mdDown: {
    display: 'none'
  },
  _pcMobile: {
    display: 'none'
  }
});
const termsFeesWrapperClass = css({
  textAlign: 'center'
});
type ApplyNowAnchorProps = ComponentPropsWithRef<'a'>;
function createApplyNowLinkComponent(linkClass: string) {
  const ApplyNowLink = t0 => {
    const $ = _c(10);
    let className;
    let props;
    let ref;
    if ($[0] !== t0) {
      ({
        className,
        ref,
        ...props
      } = t0);
      $[0] = t0;
      $[1] = className;
      $[2] = props;
      $[3] = ref;
    } else {
      className = $[1];
      props = $[2];
      ref = $[3];
    }
    let t1;
    if ($[4] !== className) {
      t1 = cx(linkClass, className);
      $[4] = className;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
      t2 = <a ref={ref} className={t1} {...props} />;
      $[6] = props;
      $[7] = ref;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  };
  ApplyNowLink.displayName = 'ApplyNowLink';
  return ApplyNowLink;
}
export const PrimaryLink = createApplyNowLinkComponent(primaryLinkClass);
export const SecondaryLink = createApplyNowLinkComponent(secondaryLinkClass);
const PhoneNumberLink = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cx(phoneNumberLinkClass, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <a ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
const NewWindowIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <MaskIcon glyph="open-in-new" css={{
      boxSize: "icon.small"
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const ApplyNowButton = t0 => {
  const $ = _c(76);
  let analytics;
  let applyLink;
  let applyPhoneNumber;
  let bottomTextOverride;
  let buttonTextOverride;
  let className;
  let cssProp;
  let ctaType;
  let device;
  let hideIcon;
  let id;
  let institutionName;
  let isActive;
  let isMonetizing;
  let linkCss;
  let name;
  let overrideShoppingFinishedId;
  let props;
  let reviewLink;
  let t1;
  let t2;
  let t3;
  let termsFeesRedirectLink;
  let versionId;
  if ($[0] !== t0) {
    ({
      id,
      applyLink,
      applyPhoneNumber,
      institutionName,
      isActive,
      isMonetizing,
      reviewLink,
      termsFeesRedirectLink,
      hideIcon,
      linkCss,
      analytics,
      ctaType,
      versionId,
      overrideShoppingFinishedId,
      hideOnMobile: t1,
      hideBelowContent: t2,
      buttonTextOverride,
      bottomTextOverride,
      device,
      isSpanFullWidth: t3,
      name,
      className,
      css: cssProp,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = analytics;
    $[2] = applyLink;
    $[3] = applyPhoneNumber;
    $[4] = bottomTextOverride;
    $[5] = buttonTextOverride;
    $[6] = className;
    $[7] = cssProp;
    $[8] = ctaType;
    $[9] = device;
    $[10] = hideIcon;
    $[11] = id;
    $[12] = institutionName;
    $[13] = isActive;
    $[14] = isMonetizing;
    $[15] = linkCss;
    $[16] = name;
    $[17] = overrideShoppingFinishedId;
    $[18] = props;
    $[19] = reviewLink;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = termsFeesRedirectLink;
    $[24] = versionId;
  } else {
    analytics = $[1];
    applyLink = $[2];
    applyPhoneNumber = $[3];
    bottomTextOverride = $[4];
    buttonTextOverride = $[5];
    className = $[6];
    cssProp = $[7];
    ctaType = $[8];
    device = $[9];
    hideIcon = $[10];
    id = $[11];
    institutionName = $[12];
    isActive = $[13];
    isMonetizing = $[14];
    linkCss = $[15];
    name = $[16];
    overrideShoppingFinishedId = $[17];
    props = $[18];
    reviewLink = $[19];
    t1 = $[20];
    t2 = $[21];
    t3 = $[22];
    termsFeesRedirectLink = $[23];
    versionId = $[24];
  }
  const hideOnMobile = t1 === undefined ? false : t1;
  const hideBelowContent = t2 === undefined ? false : t2;
  const isSpanFullWidth = t3 === undefined ? false : t3;
  let link = applyLink;
  let text = buttonTextOverride ?? "Apply Now";
  let isReview = false;
  if (!isActive || !isMonetizing || !applyLink) {
    link = reviewLink;
    text = "Read Review";
    isReview = true;
    hideIcon = true;
  }
  const sectionName = analytics.section?.name || "default";
  const phoneNumberId = device ? `${device}-${id}-${sectionName}-phone-number-link` : `${id}-${sectionName}-phone-number-link`;
  if (!link) {
    return null;
  }
  const issuerName = /s$/.test(institutionName) ? `${institutionName}'` : `${institutionName}'s`;
  const website = institutionName?.toLowerCase() === "citibank" ? "application" : "website";
  const phoneText = applyPhoneNumber ? "or call " : "";
  const bottomText = bottomTextOverride ?? `on ${issuerName} ${website}${applyPhoneNumber ? "," : ""}`;
  const t4 = id;
  const t5 = props as typeof props & {
    innerRef?: unknown;
  };
  let t6;
  if ($[25] !== t5) {
    t6 = _temp(t5);
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== className || $[28] !== cssProp || $[29] !== isSpanFullWidth) {
    t7 = cx(css(applyNowWrapperBaseRaw, {
      width: isSpanFullWidth ? "[100%]" : "auto"
    }, cssProp), className);
    $[27] = className;
    $[28] = cssProp;
    $[29] = isSpanFullWidth;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const t8 = isReview ? undefined : "APPLY_NOW_BUTTON";
  let t9;
  if ($[31] !== analytics || $[32] !== t8) {
    t9 = {
      ...analytics,
      linkType: t8
    };
    $[31] = analytics;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const t10 = ctaType ?? "APPLY_NOW_BUTTON";
  const t11 = link;
  const t12 = isReview ? undefined : "nofollow";
  const t13 = isReview ? "_self" : "_blank";
  let t14;
  if ($[34] !== linkCss) {
    t14 = css(linkCss);
    $[34] = linkCss;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = isReview ? FinishType.InternalContent : FinishType.ExternalApplication;
  let t16;
  if ($[36] !== isReview) {
    t16 = isReview ? [] : [CTASearchParams.MONETIZING, CTASearchParams.SHOPPING_FINISH];
    $[36] = isReview;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== hideIcon) {
    t17 = !hideIcon && <NewWindowIcon />;
    $[38] = hideIcon;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== bottomText || $[41] !== hideBelowContent || $[42] !== isReview) {
    t18 = !isReview && !hideBelowContent && <VisuallyHidden>{bottomText}</VisuallyHidden>;
    $[40] = bottomText;
    $[41] = hideBelowContent;
    $[42] = isReview;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== isReview || $[45] !== link || $[46] !== overrideShoppingFinishedId || $[47] !== t10 || $[48] !== t12 || $[49] !== t13 || $[50] !== t14 || $[51] !== t15 || $[52] !== t16 || $[53] !== t17 || $[54] !== t18 || $[55] !== t9 || $[56] !== text) {
    t19 = <CtaLink analytics={t9} type={t10} href={t11} Component={PrimaryLink} rel={t12} target={t13} className={t14} finishType={t15} isReview={isReview} parameterActions={t16} shoppingFinishedId={overrideShoppingFinishedId}>{text}{t17}{t18}</CtaLink>;
    $[44] = isReview;
    $[45] = link;
    $[46] = overrideShoppingFinishedId;
    $[47] = t10;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
    $[55] = t9;
    $[56] = text;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== analytics || $[59] !== applyPhoneNumber || $[60] !== bottomText || $[61] !== hideBelowContent || $[62] !== hideOnMobile || $[63] !== isReview || $[64] !== name || $[65] !== phoneNumberId || $[66] !== phoneText || $[67] !== termsFeesRedirectLink || $[68] !== versionId) {
    t20 = !isReview && !hideBelowContent && <Flex direction="column"><div className={bottomTextRowClass}><Typography variant="body.small" className={bottomTextClass}>{bottomText}</Typography><Typography id={phoneNumberId} variant="body.small" className={bottomTextClass}>{phoneText && <> {phoneText}</>}{applyPhoneNumber && <PhoneNumberLink href={`tel:${applyPhoneNumber}`} aria-labelledby={phoneNumberId}>{applyPhoneNumber}</PhoneNumberLink>}</Typography></div><div className={termsFeesWrapperClass}>{termsFeesRedirectLink && <div className={hideOnMobile ? termsFeesHiddenOnMobileClass : termsFeesVisibleOnMobileClass}><RatesAndFeesLink analytics={analytics} href={termsFeesRedirectLink} versionId={versionId} onClickAnalyticsCallback={_temp2} containerCssProps={css.raw({
            fontSize: {
              base: "[13px]",
              _pcMd: "[12px]"
            }
          })} name={name} /></div>}</div></Flex>;
    $[58] = analytics;
    $[59] = applyPhoneNumber;
    $[60] = bottomText;
    $[61] = hideBelowContent;
    $[62] = hideOnMobile;
    $[63] = isReview;
    $[64] = name;
    $[65] = phoneNumberId;
    $[66] = phoneText;
    $[67] = termsFeesRedirectLink;
    $[68] = versionId;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== id || $[71] !== t19 || $[72] !== t20 || $[73] !== t6 || $[74] !== t7) {
    t21 = <Stack id={t4} {...t6} direction="column" gap="2" className={t7}>{t19}{t20}</Stack>;
    $[70] = id;
    $[71] = t19;
    $[72] = t20;
    $[73] = t6;
    $[74] = t7;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  return t21;
};
export type ApplyNowButtonProps = Partial<ComponentPropsWithRef<'div'>> & WithCss & {
  id: string;
  applyLink?: string;
  applyPhoneNumber?: string;
  institutionName: string;
  isActive?: boolean;
  isMonetizing?: boolean;
  reviewLink?: string;
  termsFeesRedirectLink?: string;
  hideIcon?: boolean;
  linkCss?: NonNullable<WithCss['css']>;
  analytics: CtaAnalyticsProps;
  ctaType?: LinkType;
  versionId: number;
  hideOnMobile?: boolean;
  overrideShoppingFinishedId?: string;
  hideBelowContent?: boolean;
  buttonTextOverride?: string;
  bottomTextOverride?: string;
  device?: 'mobile' | 'desktop';
  isSpanFullWidth?: boolean;
  name: string;
};
function _temp(t0) {
  const {
    innerRef: _,
    ...rest
  } = t0;
  return rest;
}
function _temp2() {}